/**
 * Format citations in MLA style
 * @param {Array} sources - Array of source objects with url, title, etc.
 * @returns {string} - MLA-formatted citations string
 */
export function formatCitationsMLA(sources) {
  if (!sources) {
    return "";
  }

  // Extract sources array from different possible structures
  let sourceArray = null;

  // Handle array directly
  if (Array.isArray(sources)) {
    sourceArray = sources;
  }
  // Handle object with data property
  else if (sources.data && Array.isArray(sources.data)) {
    sourceArray = sources.data;
  }
  // Handle object with sources property
  else if (sources.sources && Array.isArray(sources.sources)) {
    sourceArray = sources.sources;
  }
  // Handle object with result.data (nested structure)
  else if (sources.result?.data && Array.isArray(sources.result.data)) {
    sourceArray = sources.result.data;
  }
  // Handle object with result.result.data (deeply nested structure)
  else if (
    sources.result?.result?.data &&
    Array.isArray(sources.result.result.data)
  ) {
    sourceArray = sources.result.result.data;
  }
  // If it's an object but not an array, try to use it as a single source
  else if (typeof sources === "object" && sources.url) {
    sourceArray = [sources];
  }

  if (!sourceArray || !Array.isArray(sourceArray) || sourceArray.length === 0) {
    return "";
  }

  const citations = sourceArray.map((source, index) => {
    const url = source.url || "";
    let title = source.title || "";

    // If no title, try to extract a meaningful title from the URL path
    if (!title) {
      try {
        const urlObj = new URL(url);
        const pathSegments = urlObj.pathname
          .split("/")
          .filter((seg) => seg && seg.length > 2)
          .map((seg) => decodeURIComponent(seg));

        if (pathSegments.length > 0) {
          // Prefer the longest segment (usually contains the article title)
          const sortedSegments = pathSegments.sort(
            (a, b) => b.length - a.length
          );
          const bestSegment = sortedSegments[0];

          // Format the segment as a title
          const extractedTitle = bestSegment
            .replace(/[-_]/g, " ")
            .replace(/\.[^.]+$/, "") // Remove file extension
            .replace(/^\d+[-_]?/, "") // Remove leading numbers/IDs
            .trim();

          // Only use if it's meaningful (not just a short ID or code)
          if (extractedTitle && extractedTitle.length > 10) {
            // Capitalize first letter of each word
            title = extractedTitle
              .split(" ")
              .map((word) => {
                // Preserve acronyms
                if (word.match(/^[A-Z]{2,}$/)) return word;
                return (
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                );
              })
              .join(" ");
          } else if (
            extractedTitle &&
            extractedTitle.length > 3 &&
            extractedTitle.length <= 10
          ) {
            // Short but potentially meaningful (like article IDs)
            title = extractedTitle.toUpperCase();
          }
        }
      } catch (e) {
        // URL parsing failed, skip title extraction
      }
    }

    // Extract domain from URL for MLA format
    let domain = "";
    let websiteName = "";
    try {
      const urlObj = new URL(url);
      domain = urlObj.hostname.replace("www.", "");
      // Format website name: capitalize first letter of each word
      websiteName =
        domain
          .split(".")
          .slice(0, -1) // Remove TLD
          .join(" ")
          .split(/[\s-]/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ") || domain;
    } catch (e) {
      // If URL parsing fails, try to extract domain manually
      const match = url.match(/https?:\/\/(?:www\.)?([^/]+)/);
      if (match) {
        domain = match[1];
        websiteName = domain.split(".")[0] || domain;
      } else {
        websiteName = "Website";
      }
    }

    // MLA format for web sources: "Title of Web Page." Website Name, URL.
    // If no title: Website Name, URL.
    const citationNumber = index + 1;
    const formattedTitle = title ? `"${title}."` : "";

    // MLA format: [Number] "Title." Website Name, URL.
    // If no title: [Number] Website Name, URL.
    if (formattedTitle) {
      return `[${citationNumber}] ${formattedTitle} ${websiteName}, ${url}.`;
    } else {
      return `[${citationNumber}] ${websiteName}, ${url}.`;
    }
  });

  return citations.join("\n");
}

/**
 * Format citations as markdown with clickable links
 * @param {Array} sources - Array of source objects with url, title, etc.
 * @returns {string} - Markdown-formatted citations string with links
 */
export function formatCitationsMarkdown(sources) {
  if (!sources) {
    return "";
  }

  // Extract sources array from different possible structures
  let sourceArray = null;

  // Handle array directly
  if (Array.isArray(sources)) {
    sourceArray = sources;
  }
  // Handle object with data property
  else if (sources.data && Array.isArray(sources.data)) {
    sourceArray = sources.data;
  }
  // Handle object with sources property
  else if (sources.sources && Array.isArray(sources.sources)) {
    sourceArray = sources.sources;
  }
  // Handle object with result.data (nested structure)
  else if (sources.result?.data && Array.isArray(sources.result.data)) {
    sourceArray = sources.result.data;
  }
  // Handle object with result.result.data (deeply nested structure)
  else if (
    sources.result?.result?.data &&
    Array.isArray(sources.result.result.data)
  ) {
    sourceArray = sources.result.result.data;
  }
  // If it's an object but not an array, try to use it as a single source
  else if (typeof sources === "object" && sources.url) {
    sourceArray = [sources];
  }

  if (!sourceArray || !Array.isArray(sourceArray) || sourceArray.length === 0) {
    return "";
  }

  const citations = sourceArray.map((source, index) => {
    const url = source.url || "";
    let title = source.title || "";

    // If no title, try to extract a meaningful title from the URL path
    if (!title) {
      try {
        const urlObj = new URL(url);
        const pathSegments = urlObj.pathname
          .split("/")
          .filter((seg) => seg && seg.length > 2)
          .map((seg) => decodeURIComponent(seg));

        if (pathSegments.length > 0) {
          // Prefer the longest segment (usually contains the article title)
          const sortedSegments = pathSegments.sort(
            (a, b) => b.length - a.length
          );
          const bestSegment = sortedSegments[0];

          // Format the segment as a title
          const extractedTitle = bestSegment
            .replace(/[-_]/g, " ")
            .replace(/\.[^.]+$/, "") // Remove file extension
            .replace(/^\d+[-_]?/, "") // Remove leading numbers/IDs
            .trim();

          // Only use if it's meaningful (not just a short ID or code)
          if (extractedTitle && extractedTitle.length > 10) {
            // Capitalize first letter of each word
            title = extractedTitle
              .split(" ")
              .map((word) => {
                // Preserve acronyms
                if (word.match(/^[A-Z]{2,}$/)) return word;
                return (
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                );
              })
              .join(" ");
          } else if (
            extractedTitle &&
            extractedTitle.length > 3 &&
            extractedTitle.length <= 10
          ) {
            // Short but potentially meaningful (like article IDs)
            title = extractedTitle.toUpperCase();
          }
        }
      } catch (e) {
        // URL parsing failed, skip title extraction
      }
    }

    // Extract domain from URL for display
    let domain = "";
    let websiteName = "";
    try {
      const urlObj = new URL(url);
      domain = urlObj.hostname.replace("www.", "");
      // Format website name: capitalize first letter of each word
      websiteName =
        domain
          .split(".")
          .slice(0, -1) // Remove TLD
          .join(" ")
          .split(/[\s-]/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ") || domain;
    } catch (e) {
      // If URL parsing fails, try to extract domain manually
      const match = url.match(/https?:\/\/(?:www\.)?([^/]+)/);
      if (match) {
        domain = match[1];
        websiteName = domain.split(".")[0] || domain;
      } else {
        websiteName = "Website";
      }
    }

    const citationNumber = index + 1;

    // Format as markdown: [Number] "Title." Website Name, [URL](url).
    // If no title: [Number] Website Name, [URL](url).
    // Note: [Number] is plain text, not a link. Only the URL is a link.
    // Since Sources section Markdown component has sources={null}, citation preprocessing won't run
    // So we can use plain [1] without escaping
    if (title) {
      // Use title as plain text, URL as the link
      return `[${citationNumber}] "${title}." ${websiteName}, [${url}](${url}).`;
    } else {
      // Use URL as both link text and link
      return `[${citationNumber}] ${websiteName}, [${url}](${url}).`;
    }
  });

  // Join with double newline to ensure each citation is on its own line/paragraph
  // Each citation will be rendered as a separate paragraph
  return citations.join("\n\n");
}

/**
 * Get the full text with sources for copying
 * @param {string} content - The message content (may be markdown)
 * @param {Array|Object} sources - Sources array or object
 * @param {boolean} stripMarkdown - Whether to strip markdown formatting (default: true for plain text copy)
 * @returns {string} - Content with MLA-formatted sources appended
 */
export function getContentWithSources(content, sources, stripMarkdown = true) {
  if (!content) return "";

  const mlaCitations = formatCitationsMLA(sources);
  if (!mlaCitations) {
    return stripMarkdown ? stripMarkdownFormatting(content) : content;
  }

  // Strip markdown from content for plain text copy
  const plainContent = stripMarkdown
    ? stripMarkdownFormatting(content)
    : content;

  return `${plainContent}\n\n## Sources\n\n${mlaCitations}`;
}

/**
 * Strip markdown formatting from text
 * @param {string} markdown - Markdown text
 * @returns {string} - Plain text with markdown removed
 */
function stripMarkdownFormatting(markdown) {
  if (!markdown) return "";

  let text = markdown;

  // Remove markdown headers (keep the text)
  text = text.replace(/^#{1,6}\s+(.+)$/gm, "$1");

  // Remove bold/italic
  text = text.replace(/\*\*([^*]+)\*\*/g, "$1");
  text = text.replace(/\*([^*]+)\*/g, "$1");
  text = text.replace(/__([^_]+)__/g, "$1");
  text = text.replace(/_([^_]+)_/g, "$1");

  // Remove links but keep text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  // Remove inline code
  text = text.replace(/`([^`]+)`/g, "$1");

  // Remove code blocks
  text = text.replace(/```[\s\S]*?```/g, "");

  // Remove images
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, "");

  // Keep citation markers like [1][2] as they are
  // Don't remove them - they're part of the content

  // Clean up extra whitespace
  text = text.replace(/\n{3,}/g, "\n\n");
  text = text.trim();

  return text;
}
